/**
 * Accounting · Multi-Clinic P&L
 *
 * Quick P&L for N selected clinics across two periods — weeks, months,
 * quarters or any two date ranges — shown side by side. The lines:
 *   1. Orders Processed  (total order count)
 *   2. Topline Revenue   (payment.amount)
 *   3. Shipping          (orders × dated rate)
 *   4. Pharmacy COGS     (curated drug price, pharmacy-matched)
 *   5. New Sales         (first-order revenue $ + new order count #)
 *   6. Avg $ / Sale      (revenue ÷ orders)
 *
 * Revenue/COGS/shipping use the exact same basis as the dashboard P&L.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { format } from 'date-fns'
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts'
import {
  Card, CardHeader, CardTitle, CardContent, Button,
  Popover, PopoverTrigger, PopoverContent, Calendar, Badge,
} from '../components/ui'
import { accountingAPI } from '../services/api'
import ClinicMultiSelect from '../components/ClinicMultiSelect'
import { DateRangePicker } from '../components/DateRangePicker'
import {
  PERIOD_TYPES, defaultPeriods, periodOptions, periodLabel, periodNoun, periodDays,
  isPartial, periodParams, weekPeriod,
} from '../lib/pnlPeriods'
import { useAuthStore } from '../stores/authStore'
import {
  DollarSign, Truck, Building2, ShoppingCart, TrendingUp, Package, ClipboardList,
  Calendar as CalendarIcon, RefreshCw, AlertCircle, Loader2, Info,
} from 'lucide-react'

function formatCurrency(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format((cents || 0) / 100)
}

function formatNumber(num) {
  return new Intl.NumberFormat('en-US').format(num || 0)
}

// Phone widths: $1,000 and up in 3 significant figures ($301K, $12.3K, $1.23M)
// so a card's three columns fit. Smaller figures stay exact.
const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumSignificantDigits: 3 })
function formatCompactCurrency(cents) {
  const dollars = (cents || 0) / 100
  if (Math.abs(dollars) < 1000) return formatCurrency(cents)
  return `${dollars < 0 ? '-' : ''}$${compact.format(Math.abs(dollars))}`
}
function formatCompactNumber(num) {
  return Math.abs(num || 0) < 10000 ? formatNumber(num) : compact.format(num)
}

/** Full figure from sm up, compact below it; the full one stays in the title. */
function Fig({ value, count = false }) {
  const full = count ? formatNumber(value) : formatCurrency(value)
  const short = count ? formatCompactNumber(value) : formatCompactCurrency(value)
  if (full === short) return full
  return (
    <span title={full}>
      <span className="sm:hidden">{short}</span>
      <span className="hidden sm:inline">{full}</span>
    </span>
  )
}

// The P&L lines. goodWhenUp drives delta coloring (costs are red when up).
// Two order counts are shown because revenue and cost live on different cohorts:
//   - Orders Placed    = revenue cohort (paid charges/invoices)
//   - Orders Processed = cost cohort (pharmacy fills)
// perProcessed metrics (shipping, COGS) divide by Orders Processed so a per-fill
// cost reads true; the weekly total shows as subtext. avg_per_sale divides
// revenue by Orders Placed. For clinics with no distinct fill count the two are
// equal.
const METRICS = [
  { key: 'total_orders', label: 'Orders Placed', icon: ClipboardList, goodWhenUp: true, isCount: true },
  { key: 'orders_processed', label: 'Orders Processed', icon: Package, goodWhenUp: true, isCount: true },
  { key: 'topline_rev', label: 'Topline Revenue', icon: DollarSign, goodWhenUp: true },
  { key: 'shipping', label: 'Shipping / Order', icon: Truck, goodWhenUp: false, perProcessed: true },
  { key: 'pharmacy_cogs', label: 'COGS / Order', icon: Building2, goodWhenUp: false, perProcessed: true },
  { key: 'new_sales', label: 'New Revenue', icon: ShoppingCart, goodWhenUp: true, ordersKey: 'new_orders' },
  { key: 'recurring_sales', label: 'Recurring Revenue', icon: RefreshCw, goodWhenUp: true, ordersKey: 'recurring_orders' },
  // Recurring, split the Daily Report's way (backend services/rebills): a
  // Rebill bills a subscription already billed once; a Plan Change is any other
  // repeat charge. null where a clinic's P&L does not split it.
  { key: 'rebill_sales', label: 'Rebills', goodWhenUp: true, ordersKey: 'rebill_orders', splitLine: true },
  { key: 'plan_change_sales', label: 'Plan Changes', goodWhenUp: true, ordersKey: 'plan_change_orders', splitLine: true },
  { key: 'avg_per_sale', label: 'Avg $ / Sale', icon: TrendingUp, goodWhenUp: true },
]

// Resolve a metric's displayed value for a week line. perProcessed metrics divide
// the weekly total by that week's Orders Processed (fill) count — a per-fill cost
// (0 when there are no fills).
function metricValue(m, line) {
  if (!line) return 0
  if (m.perProcessed) {
    const processed = line.orders_processed || 0
    return processed > 0 ? Math.round((line[m.key] || 0) / processed) : 0
  }
  return line[m.key] || 0
}

function DeltaCell({ a, b, goodWhenUp, isCurrency = true }) {
  if (b === 0 && a === 0) return <span className="text-muted-foreground">—</span>
  const diff = a - b
  const pct = b !== 0 ? (diff / Math.abs(b)) * 100 : null
  const up = diff > 0
  const neutral = diff === 0
  const good = neutral ? null : up === goodWhenUp
  const color = neutral ? 'text-muted-foreground' : good ? 'text-emerald-600' : 'text-red-600'
  const sign = up ? '+' : ''
  return (
    <span className={`font-medium ${color}`}>
      {sign}<Fig value={diff} count={!isCurrency} />
      {pct !== null && (
        <span className="text-xs ml-1 opacity-80">({sign}{pct.toFixed(0)}%)</span>
      )}
    </span>
  )
}

// Cost lines that are dashed out (rendered "—") when a clinic reports on a
// net-revenue basis with no tracked COGS/shipping (e.g. Bloom).
const COST_METRIC_KEYS = new Set(['shipping', 'pharmacy_cogs'])

/** A single 5-line P&L card (per clinic, or the all-selected totals). */
function PnLCard({ title, color, noun = 'Week', weekA, weekB, weekARange, weekBRange, emphasized = false, costsExcluded = false, revenueNote = null }) {
  return (
    <Card className={emphasized ? 'border-primary/40 bg-primary/5' : ''}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          {color && <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: color }} />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-muted-foreground border-b">
              <th className="text-left font-medium py-1.5"></th>
              <th className="text-right font-medium py-1.5">
                {noun} A
                {weekARange && (
                  <span className="block text-[10px] font-normal opacity-70">{weekARange}</span>
                )}
              </th>
              <th className="text-right font-medium py-1.5">
                {noun} B
                {weekBRange && (
                  <span className="block text-[10px] font-normal opacity-70">{weekBRange}</span>
                )}
              </th>
              <th className="text-right font-medium py-1.5">Δ</th>
            </tr>
          </thead>
          <tbody>
            {METRICS.map((m) => {
              const Icon = m.icon
              const a = metricValue(m, weekA)
              const b = metricValue(m, weekB)
              // Cost lines this clinic doesn't track (net-revenue basis) render as
              // a muted dash rather than $0, so a real zero isn't implied.
              const dashed = (costsExcluded && COST_METRIC_KEYS.has(m.key)) ||
                (m.splitLine && (weekA?.[m.key] == null || weekB?.[m.key] == null))
              const Dash = () => <span className="text-muted-foreground">—</span>
              return (
                <tr key={m.key} className="border-b last:border-0">
                  <td className="py-2 text-muted-foreground">
                    <span className={`flex items-center gap-1.5 ${m.splitLine ? 'pl-5 text-xs' : ''}`}>
                      {Icon && <Icon className="w-3.5 h-3.5 opacity-60" />}
                      {m.splitLine && '↳ '}{m.label}
                    </span>
                  </td>
                  <td className="py-2 text-right font-semibold tabular-nums">
                    {dashed ? <Dash /> : <Fig value={a} count={m.isCount} />}
                    {!dashed && m.ordersKey && (
                      <span className="block text-xs font-normal text-muted-foreground">
                        <Fig value={weekA?.[m.ordersKey]} count /> orders
                      </span>
                    )}
                    {!dashed && m.perProcessed && (
                      <span className="block text-xs font-normal text-muted-foreground">
                        <Fig value={weekA?.[m.key]} /> total
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {dashed ? <Dash /> : <Fig value={b} count={m.isCount} />}
                    {!dashed && m.ordersKey && (
                      <span className="block text-xs text-muted-foreground">
                        <Fig value={weekB?.[m.ordersKey]} count /> orders
                      </span>
                    )}
                    {!dashed && m.perProcessed && (
                      <span className="block text-xs text-muted-foreground">
                        <Fig value={weekB?.[m.key]} /> total
                      </span>
                    )}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {dashed ? <Dash /> : <DeltaCell a={a} b={b} goodWhenUp={m.goodWhenUp} isCurrency={!m.isCount} />}
                    {!dashed && m.ordersKey && (
                      <span className="block text-xs">
                        <DeltaCell
                          a={weekA?.[m.ordersKey] || 0}
                          b={weekB?.[m.ordersKey] || 0}
                          goodWhenUp
                          isCurrency={false}
                        />
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {revenueNote && (
          <div className="mt-3 flex items-start gap-1.5 rounded-md bg-muted/50 px-2.5 py-2 text-xs text-muted-foreground">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-70" />
            <span>{revenueNote}</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/** Picks one comparison period in the style of the chosen type. */
function PeriodPicker({ label, type, value, onChange }) {
  const [open, setOpen] = useState(false)
  if (type === 'month' || type === 'quarter') {
    const options = periodOptions(type)
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        <select
          className="flex h-10 w-full sm:w-56 rounded-md border border-input bg-background px-3 text-sm"
          value={format(value.from, 'yyyy-MM-dd')}
          onChange={e => onChange(options.find(o => o.value === e.target.value).period)}
        >
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    )
  }
  if (type === 'custom') {
    return (
      <div className="space-y-1">
        <label className="text-xs font-medium text-muted-foreground">{label}</label>
        <DateRangePicker
          className="w-full sm:w-auto"
          dateRange={value}
          onDateRangeChange={range => { if (range?.from && range?.to) onChange({ from: range.from, to: range.to }) }}
        />
      </div>
    )
  }
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full sm:w-56 justify-start font-normal">
            <CalendarIcon className="w-4 h-4 mr-2 opacity-60" />
            {periodLabel('week', value)}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <Calendar
            mode="single"
            selected={value.from}
            onSelect={(d) => { if (d) { onChange(weekPeriod(d)); setOpen(false) } }}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

function PnLTrend({ data, loading, error }) {
  const monthLabel = (date) => format(new Date(date + 'T12:00:00'), 'MMM yyyy')
  const hasOtherRevenue = data?.months.some(month => month.other_revenue !== 0)
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Aggregate P&L · Trailing 6 Months</CardTitle>
        <p className="text-xs text-muted-foreground">
          Monthly totals for the six most recent completed calendar months (Eastern Time).
          {data && ` ${data.clinics_included.length} clinic(s) included · ${monthLabel(data.months[0].start)} – ${monthLabel(data.months[5].start)}.`}
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading six-month P&L…
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 py-6 text-red-600 text-sm" role="alert">
            <AlertCircle className="w-4 h-4" /> {error} Click Run to retry.
          </div>
        ) : data && (
          <>
            {data.errors.length > 0 && (
              <p className="mb-3 text-sm text-amber-700" role="alert">
                Partial totals — excluded clinics: {data.errors.map(e => e.clinicId).join(', ')}.
              </p>
            )}
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={data.months} margin={{ top: 10, right: 12, left: 8, bottom: 5 }} accessibilityLayer stackOffset="sign">
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="start" tickFormatter={monthLabel} tick={{ fontSize: 11 }} minTickGap={20} />
                <YAxis tickFormatter={value => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(value / 100)} tick={{ fontSize: 11 }} width={65} />
                <Tooltip labelFormatter={monthLabel} formatter={(value, name) => [formatCurrency(value), name]} contentStyle={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine y={0} stroke="#94a3b8" />
                <Bar dataKey="new_revenue" name="New revenue" stackId="revenue" fill="#3b82f6" maxBarSize={60} />
                <Bar dataKey="recurring_revenue" name="Recurring revenue" stackId="revenue" fill="#8b5cf6" maxBarSize={60} />
                {hasOtherRevenue && <Bar dataKey="other_revenue" name="Other revenue" stackId="revenue" fill="#94a3b8" maxBarSize={60} />}
                <Line type="linear" dataKey="revenue" name="Total revenue" stroke="#475569" strokeWidth={2} strokeDasharray="5 3" />
                <Line type="linear" dataKey="expenses" name="Expenses" stroke="#f59e0b" strokeWidth={2} />
                <Line type="linear" dataKey="profit" name="Profit" stroke="#10b981" strokeWidth={3} />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm tabular-nums">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground">
                    <th scope="col" className="py-2 text-left font-medium">Month</th>
                    {['New revenue', 'Recurring revenue', 'Rebills', 'Plan changes', ...(hasOtherRevenue ? ['Other revenue'] : []), 'Total revenue', 'Recurring share'].map(label => (
                      <th key={label} scope="col" className="py-2 pl-4 text-right font-medium whitespace-nowrap">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.months.map(month => (
                    <tr key={month.start} className="border-b last:border-0">
                      <th scope="row" className="py-2 text-left font-medium whitespace-nowrap">{monthLabel(month.start)}</th>
                      <td className="py-2 pl-4 text-right"><Fig value={month.new_revenue} /></td>
                      <td className="py-2 pl-4 text-right"><Fig value={month.recurring_revenue} /></td>
                      <td className="py-2 pl-4 text-right text-muted-foreground">{month.rebill_revenue == null ? '—' : <Fig value={month.rebill_revenue} />}</td>
                      <td className="py-2 pl-4 text-right text-muted-foreground">{month.plan_change_revenue == null ? '—' : <Fig value={month.plan_change_revenue} />}</td>
                      {hasOtherRevenue && <td className="py-2 pl-4 text-right"><Fig value={month.other_revenue} /></td>}
                      <td className="py-2 pl-4 text-right font-semibold"><Fig value={month.revenue} /></td>
                      <td className="py-2 pl-4 text-right">{month.revenue > 0 ? `${(month.recurring_revenue / month.revenue * 100).toFixed(1)}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasOtherRevenue && <p className="mt-3 text-xs text-muted-foreground">Other revenue is collected revenue outside the new and recurring categories, including cancelled or unattributed sales.</p>}
            <p className="mt-3 text-xs text-muted-foreground">Recurring revenue is every charge from a returning customer. Rebills are charges on a subscription that was already billed once; plan changes are the rest (a new plan, a dose step-up on a new subscription, an add-on). The two sum to recurring revenue, and match the Daily Report.</p>
            <p className="mt-3 text-xs text-muted-foreground">Profit uses the dashboard P&L basis: revenue less tracked expenses, including pharmacy COGS, shipping, and ad spend.</p>
            {data.notes.map(note => (
              <p key={note.clinicId} className="mt-2 text-xs text-muted-foreground">{note.displayName}: {note.note}</p>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export default function MultiPnLPage() {
  const { clinics } = useAuthStore()

  const [selectedClinics, setSelectedClinics] = useState(() => clinics.map((c) => c.id))
  // Default to Monday–Sunday weeks: A = last complete week, B = the week
  // before. Switching type resets both to the last complete period of that
  // type and the one before it (see lib/pnlPeriods).
  const [periodType, setPeriodType] = useState('week')
  const [periodA, setPeriodA] = useState(() => defaultPeriods('week')[0])
  const [periodB, setPeriodB] = useState(() => defaultPeriods('week')[1])
  const changePeriodType = (type) => {
    const [a, b] = defaultPeriods(type)
    setPeriodType(type)
    setPeriodA(a)
    setPeriodB(b)
  }

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [trend, setTrend] = useState(null)
  const [trendLoading, setTrendLoading] = useState(false)
  const [trendError, setTrendError] = useState(null)
  const [autoFetched, setAutoFetched] = useState(false)

  // Keep selection in sync if the clinic list arrives after mount
  useEffect(() => {
    if (clinics.length && selectedClinics.length === 0) {
      setSelectedClinics(clinics.map((c) => c.id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinics])

  const fetchData = useCallback(async () => {
    if (selectedClinics.length === 0) {
      setData(null)
      setTrend(null)
      setTrendError(null)
      setError('Select at least one clinic.')
      return
    }
    setLoading(true)
    setError(null)
    setTrendLoading(true)
    setTrend(null)
    setTrendError(null)
    const trendRequest = accountingAPI.getMultiPnL({
      clinicIds: selectedClinics.join(','),
      trendOnly: true,
    }).then(response => {
      setTrend(response.data.data)
    }).catch(() => {
      setTrendError('Failed to load the six-month P&L trend.')
    }).finally(() => {
      setTrendLoading(false)
    })
    try {
      const response = await accountingAPI.getMultiPnL({
        clinicIds: selectedClinics.join(','),
        ...periodParams(periodA, periodB),
      })
      // Labels follow what was fetched, not the pickers, until Run is pressed again.
      setData({ ...response.data.data, periodType, periodA, periodB })
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load P&L data')
      setData(null)
    } finally {
      setLoading(false)
    }
    await trendRequest
  }, [selectedClinics, periodType, periodA, periodB])

  // Auto-run once, as soon as a clinic selection is available. Further changes
  // are applied via the Run button so toggling clinics doesn't hammer the API.
  useEffect(() => {
    if (!autoFetched && selectedClinics.length > 0) {
      setAutoFetched(true)
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClinics, autoFetched])

  const clinicCards = useMemo(() => data?.clinics || [], [data])
  const noun = periodNoun(data?.periodType)
  const rangeA = data ? periodLabel(data.periodType, data.periodA) : ''
  const rangeB = data ? periodLabel(data.periodType, data.periodB) : ''
  // Totals are not normalised per day, so say when the two periods differ in
  // length or one is still running.
  const periodNotes = []
  if (data) {
    const daysA = periodDays(data.periodA)
    const daysB = periodDays(data.periodB)
    if (daysA !== daysB) periodNotes.push(`${noun} A has ${daysA} days and ${noun} B has ${daysB}; figures are totals, not per day.`)
    if (isPartial(data.periodA)) periodNotes.push(`${noun} A is still in progress.`)
    if (isPartial(data.periodB)) periodNotes.push(`${noun} B is still in progress.`)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Multi-Clinic P&L</h1>
          <p className="text-sm text-muted-foreground">
            Quick P&L across clinics, comparing two weeks, months, quarters or date ranges side by side.
          </p>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 flex-wrap">
            <ClinicMultiSelect clinics={clinics} selected={selectedClinics} onChange={setSelectedClinics} />
            <div className="space-y-1">
              <span className="text-xs font-medium text-muted-foreground">Compare</span>
              <div className="flex rounded-md border border-input p-0.5" role="group" aria-label="Period type">
                {PERIOD_TYPES.map(t => (
                  <Button
                    key={t.key}
                    size="sm"
                    variant={periodType === t.key ? 'default' : 'ghost'}
                    className="h-8 px-3"
                    aria-pressed={periodType === t.key}
                    onClick={() => changePeriodType(t.key)}
                  >
                    {t.label}
                  </Button>
                ))}
              </div>
            </div>
            <PeriodPicker label={`${periodNoun(periodType)} A`} type={periodType} value={periodA} onChange={setPeriodA} />
            <PeriodPicker label={`${periodNoun(periodType)} B`} type={periodType} value={periodB} onChange={setPeriodB} />
            <Button onClick={fetchData} disabled={loading || trendLoading}>
              {loading || trendLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Run
            </Button>
          </div>
          {data && (
            <div className="flex flex-wrap gap-2 mt-3 text-xs text-muted-foreground">
              <Badge variant="outline">{noun} A: {rangeA}</Badge>
              <Badge variant="outline">{noun} B: {rangeB}</Badge>
            </div>
          )}
          {periodNotes.length > 0 && (
            <p className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-70" />
              <span>{periodNotes.join(' ')}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {(trend || trendLoading || trendError) && (
        <PnLTrend data={trend} loading={trendLoading} error={trendError} />
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {data?.errors?.length > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 text-amber-700 text-sm">
          <AlertCircle className="w-4 h-4" />
          {data.errors.length} clinic(s) failed to load: {data.errors.map((e) => e.clinicId).join(', ')}
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading P&L…
        </div>
      )}

      {data && (
        <>
          {/* Totals across all selected clinics */}
          {clinicCards.length > 1 && (
            <PnLCard
              title={`All Selected (${clinicCards.length} clinics)`}
              weekA={data.totals.weekA}
              weekB={data.totals.weekB}
              noun={noun}
              weekARange={rangeA}
              weekBRange={rangeB}
              emphasized
            />
          )}

          {/* Per-clinic cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {clinicCards.map((c) => (
              <PnLCard
                key={c.clinicId}
                title={c.displayName}
                color={c.color}
                weekA={c.weekA}
                weekB={c.weekB}
                noun={noun}
                weekARange={rangeA}
                weekBRange={rangeB}
                costsExcluded={c.costsExcluded}
                revenueNote={c.revenueNote}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
